import { useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { parametroGeneralApi } from '../api/parametroGeneralApi';

const extraerError = (error, fallback) => {
  const msg = error.response?.data?.detail || error.response?.data?.message || fallback;
  return typeof msg === 'string' ? msg : JSON.stringify(msg);
};

export const useParametrosGenerales = () => {
  const [parametros, setParametros] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchParametros = useCallback(async (buscar = '') => {
    setLoading(true);
    setError(null);
    try {
      const response = await parametroGeneralApi.listar(buscar || undefined);
      setParametros(response.data || []);
      return response.data;
    } catch (err) {
      const errorMessage = extraerError(err, 'Error al obtener parámetros');
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const createParametro = useCallback(async (data) => {
    setLoading(true);
    setError(null);
    try {
      const response = await parametroGeneralApi.crear(data);
      setParametros((prev) => [...prev, response.data].sort((a, b) => a.clave.localeCompare(b.clave)));
      toast.success('Parámetro creado exitosamente');
      return response.data;
    } catch (err) {
      const errorMessage = extraerError(err, 'Error al crear parámetro');
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateParametro = useCallback(async (id, data) => {
    setLoading(true);
    setError(null);
    try {
      const response = await parametroGeneralApi.actualizar(id, data);
      setParametros((prev) =>
        prev
          .map((p) => (p.id === id ? response.data : p))
          .sort((a, b) => a.clave.localeCompare(b.clave))
      );
      toast.success('Parámetro actualizado exitosamente');
      return response.data;
    } catch (err) {
      const errorMessage = extraerError(err, 'Error al actualizar parámetro');
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteParametro = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      await parametroGeneralApi.eliminar(id);
      setParametros((prev) => prev.filter((p) => p.id !== id));
      toast.success('Parámetro eliminado exitosamente');
      return true;
    } catch (err) {
      const errorMessage = extraerError(err, 'Error al eliminar parámetro');
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const clearError = useCallback(() => setError(null), []);

  return {
    parametros,
    loading,
    error,
    fetchParametros,
    createParametro,
    updateParametro,
    deleteParametro,
    clearError,
  };
};

export default useParametrosGenerales;
